import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { suppliers as suppliersApi } from "@/api";
import { useAuth } from "@/auth";
import TierBadge from "@/components/TierBadge";
import { TIER_META, TIER_BADGE_IMG, normalizeTier } from "@/constants";
import { useCategories } from "@/hooks/useCategories";
import { Plus, Upload } from "lucide-react";

export default function Suppliers() {
  const { user } = useAuth();
  const CATEGORIES = useCategories();
  const [list, setList] = useState([]);
  const [category, setCategory] = useState("");
  const [tier, setTier] = useState("");
  const [q, setQ] = useState("");
  const [pilotCats, setPilotCats] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const isAdmin = ["admin", "owner"].includes(user?.role);

  useEffect(() => {
    import("@/api").then(({ default: http }) =>
      http.get("/pilot-categories").then((r) => setPilotCats(r.data.categories || [])).catch(() => {}));
  }, []);

  const load = () => {
    suppliersApi.list({
      ...(category && { category }),
      ...(tier && { tier }),
      ...(q && { q }),
      ...(showHidden && { include_hidden: true }),
    }).then(setList);
  };
  useEffect(() => { load(); }, [category, tier, q, showHidden]);

  const reactivate = (e, s) => {
    e.preventDefault(); e.stopPropagation();
    suppliersApi.setHidden(s.id, !s.hidden).then(load);
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="font-hand text-xl text-[var(--lala-blue)]">directorio</div>
          <h1 className="font-display text-4xl">Proveedores</h1>
          <p className="text-[var(--ink-soft)] mt-1">{list.length} proveedores registrados</p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-[11px] text-[var(--ink-soft)]" data-testid="directory-legend">
            <span className="font-bold uppercase tracking-wider">Guía:</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#009639" }} /> KPIs en meta</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#F0B429" }} /> requiere seguimiento</span>
            <span className="inline-flex items-center gap-1.5"><span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#E4002B" }} /> riesgo alto en KPIs</span>
            {/* <span className="inline-flex items-center gap-1.5"><span className="inline-flex items-center rounded-full border border-emerald-600 text-emerald-700 px-1.5 text-[9px] font-bold">ECO</span> insignia de sustentabilidad</span> */}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {user?.role === "admin" && (
            <>
              <Link to="/admin/proveedores/nuevo" className="btn-primary text-sm" data-testid="new-supplier-btn">
                <Plus size={16} /> Nuevo proveedor
              </Link>
              <Link to="/admin/proveedores/carga-masiva"
                className="sketch-pill bg-[var(--cream)] text-[var(--lala-blue)] border border-[var(--lala-blue)] px-3 py-1.5 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-[var(--cream-soft)]"
                data-testid="bulk-upload-btn">
                <Upload size={14} /> Carga masiva
              </Link>
            </>
          )}
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar nombre o SAP ID…" className="input-field max-w-[240px]" data-testid="suppliers-search" />
          <select value={category} onChange={(e) => setCategory(e.target.value)} className="input-field max-w-[200px]" data-testid="suppliers-category">
            <option value="">Todas las categorías</option>
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={tier} onChange={(e) => setTier(e.target.value)} className="input-field max-w-[180px]" data-testid="suppliers-tier">
            <option value="">Niveles</option>
            {Object.entries(TIER_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
          </select>
          {isAdmin && (
            <label className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--ink-soft)] cursor-pointer self-center" data-testid="suppliers-show-hidden">
              <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
              Mostrar ocultos (fuera del piloto definitivo)
            </label>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {list.map((s) => {
          const meta = TIER_META[normalizeTier(s.current_tier)] || TIER_META.commercial;
          return (
            <Link key={s.id} to={`/proveedores/${s.id}`} className="sketch-border bg-[var(--cream-soft)] p-5 block" data-testid={`supplier-card-${s.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-hand text-sm text-[var(--lala-blue)]">{s.category}
                    {s.hidden && (
                      <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-white bg-[var(--ink-soft)] rounded-full px-2 py-0.5" data-testid={`hidden-badge-${s.id}`}>Oculto</span>
                    )}
                    {pilotCats.length > 0 && !pilotCats.includes(s.category) && (
                      <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-[var(--ink-soft)] bg-[var(--cream-deep)] rounded-full px-2 py-0.5" data-testid={`out-of-pilot-${s.id}`}>Fuera del piloto</span>
                    )}
                  </div>
                  <div className="font-display text-2xl mt-0.5">{s.name}</div>
                  {s.sap_id && (
                    <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] mt-0.5" data-testid={`supplier-sap-${s.id}`}>
                      SAP · <span className="font-semibold text-[var(--ink)]">{s.sap_id}</span>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <div className="flex items-center gap-1.5">
                    {s.risk_light && (
                      <span title={s.risk_light === "rojo" ? "Riesgo alto: KPIs críticos por debajo de la meta" : s.risk_light === "amarillo" ? "Atención: algunos KPIs requieren seguimiento" : "En orden: KPIs dentro de la meta"}
                        data-testid={`risk-light-${s.id}`}
                        className="inline-block w-3 h-3 rounded-full ring-2 ring-white shadow"
                        style={{ background: s.risk_light === "rojo" ? "#E4002B" : s.risk_light === "amarillo" ? "#F0B429" : "#009639" }} />
                    )}
                    {TIER_BADGE_IMG[normalizeTier(s.current_tier)] && (
                      <img src={TIER_BADGE_IMG[normalizeTier(s.current_tier)]} alt=""
                        title={`Medalla ${TIER_META[normalizeTier(s.current_tier)]?.gem || ""}`}
                        className="w-7 h-7 object-contain" draggable={false}
                        data-testid={`card-medal-${s.id}`} />
                    )}
                    <TierBadge tier={s.current_tier} />
                  </div>
                  {/* {s.eco_badge?.earned && <EcoBadge badge={s.eco_badge} size="sm" />} */}
                  {isAdmin && showHidden && (
                    <button onClick={(e) => reactivate(e, s)}
                      className={`text-[10px] font-bold uppercase rounded-full px-2 py-0.5 border ${s.hidden ? "border-emerald-500 text-emerald-700 hover:bg-emerald-50" : "border-[var(--line)] text-[var(--ink-soft)] hover:bg-[var(--cream-deep)]"}`}
                      data-testid={`toggle-hidden-${s.id}`}>
                      {s.hidden ? "Reactivar" : "Ocultar"}
                    </button>
                  )}
                </div>
              </div>
              {s.description && <p className="text-sm text-[var(--ink-soft)] mt-3 line-clamp-2">{s.description}</p>}
              <div className="flex items-end justify-between mt-4 pt-3 border-t border-[var(--line)]">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)]">Score</div>
                  <div className="font-display text-3xl" style={{ color: meta.color }}>{s.current_score?.toFixed?.(2) ?? "—"}</div>
                </div>
                <div className="text-xs text-[var(--ink-soft)]">{s.last_period || "Sin evaluación"}</div>
              </div>
            </Link>
          );
        })}
        {list.length === 0 && (
          <div className="col-span-full text-center text-[var(--ink-soft)] py-12">No hay proveedores con esos filtros.</div>
        )}
      </div>
    </div>
  );
}
